import React from 'react';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

interface FadeInProps {
  children: React.ReactNode;
  direction?: Direction;
  delay?: number;
  duration?: number;
  distance?: number;
  className?: string;
  threshold?: number;
  /** Pass 0 to disable the safety-net premature reveal (pure scroll-reveal).
   *  Default (undefined) keeps the hook's 1.2s fallback. */
  revealAfterMs?: number;
}

export const FadeIn: React.FC<FadeInProps> = ({
  children,
  direction = 'up',
  delay = 0,
  duration = 600,
  distance = 30,
  className = '',
  threshold = 0.1,
  revealAfterMs,
}) => {
  const { ref, isVisible } = useScrollAnimation({ threshold, revealAfterMs });

  const getTransform = () => {
    if (!isVisible) {
      switch (direction) {
        case 'up': return `translateY(${distance}px)`;
        case 'down': return `translateY(-${distance}px)`;
        case 'left': return `translateX(${distance}px)`;
        case 'right': return `translateX(-${distance}px)`;
        default: return 'none';
      }
    }
    return 'none';
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: getTransform(),
        transition: `opacity ${duration}ms ease-out ${delay}ms, transform ${duration}ms ease-out ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
};

// Staggered fade-in for lists/grids - keeps it professional and subtle
interface StaggeredFadeInProps {
  children: React.ReactNode[];
  direction?: Direction;
  staggerDelay?: number;
  duration?: number;
  className?: string;
  itemClassName?: string;
  /** Pass 0 to disable the safety-net premature reveal (pure scroll-reveal). */
  revealAfterMs?: number;
}

export const StaggeredFadeIn: React.FC<StaggeredFadeInProps> = ({
  children,
  direction = 'up',
  staggerDelay = 80,
  duration = 500,
  className = '',
  itemClassName = '',
  revealAfterMs,
}) => {
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.1, revealAfterMs });

  const getTransform = (visible: boolean) => {
    if (!visible) {
      switch (direction) {
        case 'up': return 'translateY(20px)';
        case 'down': return 'translateY(-20px)';
        case 'left': return 'translateX(20px)';
        case 'right': return 'translateX(-20px)';
        default: return 'none';
      }
    }
    return 'none';
  };

  return (
    <div ref={ref} className={className}>
      {React.Children.map(children, (child, index) => (
        <div
          className={`h-full ${itemClassName}`}
          style={{
            opacity: isVisible ? 1 : 0,
            transform: getTransform(isVisible),
            transition: `opacity ${duration}ms ease-out ${index * staggerDelay}ms, transform ${duration}ms ease-out ${index * staggerDelay}ms`,
          }}
        >
          {child}
        </div>
      ))}
    </div>
  );
};
